<template>
  <div class="relative flex flex-col w-full h-full p-3 bg-light-box-background dark:bg-dark-box-background">
    <div class="flex flex-col pb-1 w-full">
      <h1
        class="flex items-center transition-colors ease-in-out duration-side-menu-transition-duration text-xl font-medium text-light-body-text dark:text-dark-body-text">
        {{ translatedLabel }}
      </h1>
      <div class="flex items-center w-full mt-2">
        <button class="-ml-0 w-fit primary-btn h-[38px]" @click="filter_is_open = !filter_is_open">
          <Funnel class="w-5 h-5 text-white mr-2" />
          {{ filter_is_open || name ? $t("Clear Filter") : $t("Add Filter") }}
        </button>
        <div class="relative flex ml-1 focus-within:z-10 w-full">
          <div class="absolute inset-y-0 left-0 z-10 flex items-center pl-3 pointer-events-none">
            <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>
          <TextInput
            :in-table-filter="true"
            input-classes="w-full appearance-none block px-3 py-2 border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            name="name"
            type="text"
            class="-mt-1"
            :validate-on-input="true"
            :icon="true"
            :label="$t('Name')"
            :value="name"
            :debounce="500"
            @update:modelValue="
              (e) => {
                name = e
              }
            " />
        </div>
        <div class="-ml-1 has-tooltip">
          <RecordActionsDropDown
            :component-reference="componentReference"
            :object-type="object_type"
            :page-reference="PAGE_REFERENCES.DASHBOARD_LEADS_TABLE"
            :selected-records="Array.from(selected_records)"
            :box-background="true"
            :disabled="editing_disabled"
            :icon-button="true" />
          <span v-if="editing_disabled"
                class="flex-wrap p-2 -ml-40 bg-gray-300 rounded tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
          </span>
        </div>
        <div v-if="!editing_disabled" class="ml-1">
          <button
            :disabled="editing_disabled"
            class="inline-flex px-2 py-2 font-medium border border-gray-200 rounded-md has-tooltip hover:bg-gray-100 sm:text-sm focus:outline-none"
            @click.passive="$emitter.emit(ALL_EMITTERS.UPDATE_LEADS_TABLE_METRICS, componentReference)">
            <Grid2x2Plus class="w-5 h-5 text-gray-500" aria-hidden="true" />
            <span v-if="editing_disabled"
                  class="flex-wrap p-2 bg-gray-300 rounded -ml-52 mt-7 tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="relative h-full min-h-[200px]">
      <div class="overflow-visible h-full">
        <div
          v-if="current_records.length == 0 && is_loading"
          class="min-h-[200px] rounded-lg bg-light-box-background dark:bg-dark-box-background flex justify-center h-full shadow">
          <div class="flex justify-center flex-col">
            <h3 class="capitalize mt-2 text-2xl font-normal text-gray-500 relative">
              {{ $t("Loading leads") }} <LoadingSpinner class="" />
            </h3>
          </div>
        </div>
        <div
          v-else-if="current_records.length == 0 && !is_loading"
          class="min-h-[200px] bg-light-box-background dark:bg-dark-box-background flex justify-center h-full shadow rounded-lg">
          <div class="flex justify-center flex-col">
            <div class="flex justify-center items-center flex-col">
              <h3 class="leading-8 mt-2 text-xl font-normal text-black dark:text-white">
                {{ $t("No new leads found at this moment!") }}
              </h3>
            </div>
          </div>
        </div>
        <div
          v-else-if="current_records.length > 0 && !is_loading && (headers?.length == 0 || headers?.length == 1)"
          class="min-h-[200px] bg-light-box-background dark:bg-dark-box-background flex justify-center h-full shadow rounded-lg">
          <div class="flex justify-center flex-col">
            <div class="flex justify-center items-center flex-col">
              <h3 class="leading-8 mt-2 text-xl font-normal text-black dark:text-white">
                {{ $t("There are no metrics included in the table. Click the button below to set up the RevSetter Leads table.") }}
              </h3>
            </div>
          </div>
        </div>
        <div v-if="current_records.length > 0 && !is_loading" class="flex flex-col h-full max-w-full rounded-md">
          <div class="flex flex-col flex-1 justify-between w-full overflow-y-auto overflow-x-auto rounded-md">
            <table id="records_table" class="min-w-full relative overflow-hidden rounded-md border-green-500">
              <thead
                v-if="filter_is_open"
                class="w-full border-1 bg-light-box-background dark:bg-dark-box-background transition ease-in-out duration-side-menu-transition-duration">
                <draggable
                  v-model="headers"
                  :component-data="{
                    type: 'transition-group',
                    name: !drag ? 'flip-list' : null
                  }"
                  v-bind="{
                    animation: 200,
                    group: 'leads header',
                    ghostClass: 'ghost',
                    chosenClass: 'chosen'
                  }"
                  handle=".drag-handle"
                  filter=".ignore-drag"
                  item-key="id"
                  class="border-b-1 dark:border-dark-divider-border text-left font-semibold text-common-grey transition ease-in-out duration-side-menu-transition-duration"
                  tag="tr"
                  @start="drag = true"
                  @end="
                    () => {
                      drag = false
                    }
                  ">
                  <template #item="{ element }">
                    <th v-if="element.id == 'checkbox'" class="px-6 py-3">
                      <div class="flex items-center">
                        <input
                          v-model="select_all_records"
                          type="checkbox"
                          class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                      </div>
                    </th>
                    <th v-else-if="element.id == 'origin'" class="px-6 py-3">
                      <MultiSelectInput
                        v-model:current-items="origin"
                        :value="origin"
                        name="plans_status"
                        :items="origins"
                        label-attr="name"
                        value-attr="value"
                        :allow-null="true"
                        :icon="status_icon"
                        :return-objects="true"
                        :label="$t('Status')"
                        classes="w-full -mt-[1px]"
                        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block px-3 py-1 mt-0.5 h-[38px] border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                        :placeholder="$t('Status')"
                        :show-label="false" />
                    </th>
                    <th v-else-if="element.id == 'type'" class="px-6 py-3">
                      <MultiSelectInput
                        v-model:current-items="type"
                        :value="type"
                        name="type"
                        :items="types"
                        label-attr="name"
                        value-attr="value"
                        :allow-null="true"
                        :icon="companies_icon"
                        :label="$t('Type')"
                        classes="w-full -mt-[1px]"
                        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block px-3 py-1 mt-0.5 h-[38px] border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                        :placeholder="$t('Type')"
                        :show-label="false" />
                    </th>
                    <th v-else-if="element.id == 'owner'" class="px-6 py-3">
                      <MultiSelectInput
                        v-model:current-items="owner"
                        :value="owner"
                        name="owner"
                        :items="$store.state.data?.users"
                        label-attr="full_name"
                        value-attr="id"
                        :allow-null="true"
                        :icon="user_menu_icon"
                        :label="$t('Owner')"
                        classes="w-full -mt-[1px]"
                        input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block px-3 py-1 mt-0.5 h-[38px] border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                        :placeholder="$t('Owner')"
                        :show-label="false" />
                    </th>
                    <th v-else-if="element.id == 'name'" class="px-6 py-3">
                      <div class="relative flex focus-within:z-10">
                        <div class="absolute inset-y-0 left-0 z-10 flex items-center pl-3 pointer-events-none">
                          <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
                        </div>
                        <TextInput
                          :in-table-filter="true"
                          input-classes="appearance-none block px-3 py-2 border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                          name="name"
                          type="text"
                          class="-mt-1"
                          :validate-on-input="true"
                          :icon="true"
                          :label="$t('Name')"
                          :value="name"
                          :debounce="500"
                          @update:modelValue="
                            (e) => {
                              name = e
                            }
                          " />
                      </div>
                    </th>
                    <th v-else-if="element.id == 'added_at'" class="px-6 py-3">
                      <DatePicker
                        v-model="date_range"
                        :is-dark="$store.state.ui.darkMode"
                        :input-debounce="500"
                        mode="range"
                        is-range
                        range
                        class="w-full min-w-[200px] bg-white dark:bg-dark-screen-background"
                        :attributes="[
                          {
                            key: 'today',
                            dot: true,
                            dates: new Date()
                          }
                        ]"
                        :popover="{ visibility: 'click' }">
                        <template #default="{ inputValue, inputEvents }">
                          <div class="relative flex-grow">
                            <svg
                              fill="none"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              class="absolute w-4 h-full mx-2 text-gray-400 pointer-events-none">
                              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>

                            <button
                              v-if="date_range && date_range.start"
                              class="absolute right-0 flex items-center justify-center w-6 h-6 mx-2 rounded-full top-2 group hover:bg-gray-300 rounderd-full"
                              type="button"
                              @click="date_range = null">
                              <X class="w-5 h-5 text-gray-400 group-hover:cursor-pointer" aria-hidden="true" />
                            </button>

                            <input
                              :value="date_range && date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                              placeholder="Due date"
                              class="pr-2 bg-white dark:bg-dark-box-background border dark:border-dark-divider-border w-full rounded placeholder-gray-400 text-gray-900 focus:ring-1 outline-none border-gray-300 px-10 py-1.5 focus:ring-blue-light focus:border-blue-light"
                              v-on="inputEvents.start || inputEvents.end"
                              @input="
                                $utils.debounce(() => {
                                  date_range = $event.target.value
                                }, 800)
                              ">
                          </div>
                        </template>
                      </DatePicker>
                    </th>
                    <th v-else class="px-6 py-3">
                      <div class="flex items-center">
                        <span>{{ element.label }}</span>
                        <component :is="element.icon" v-if="element.icon" class="ml-2 w-3.5 h-3.5" />
                      </div>
                    </th>
                  </template>
                </draggable>
              </thead>
              <thead
                v-else
                class="w-full border-1 bg-light-box-background dark:bg-dark-box-background transition ease-in-out duration-side-menu-transition-duration">
                <draggable
                  v-model="headers"
                  :component-data="{
                    type: 'transition-group',
                    name: !drag ? 'flip-list' : null
                  }"
                  v-bind="{
                    animation: 200,
                    group: 'leads header',
                    ghostClass: 'ghost',
                    chosenClass: 'chosen'
                  }"
                  handle=".drag-handle"
                  filter=".ignore-drag"
                  item-key="id"
                  class="border-b-1 dark:border-dark-divider-border text-left font-semibold text-common-grey transition ease-in-out duration-side-menu-transition-duration"
                  tag="tr"
                  @start="drag = true"
                  @end="
                    () => {
                      drag = false
                    }
                  ">
                  <template #item="{ element }">
                    <th v-if="element.id == 'checkbox'" class="px-6 py-3">
                      <div class="flex items-center">
                        <input
                          v-model="select_all_records"
                          type="checkbox"
                          class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
                      </div>
                    </th>
                    <th v-else class="px-6 py-3">
                      <div class="flex items-center">
                        <span>{{ element.label }}</span>
                        <component :is="element.icon" v-if="element.icon" class="ml-2 w-3.5 h-3.5" />
                      </div>
                    </th>
                  </template>
                </draggable>
              </thead>
              <tbody
                ref="records_table"
                class="min-w-full bg-light-box-background divide-light-divider-border dark:divide-dark-divider-border dark:bg-dark-box-background transition ease-in-out duration-side-menu-transition-duration">
                <!-- Global Table - rows -->
                <tr
                  v-for="company in displayed_records"
                  :id="company.id"
                  :key="company.id"
                  class="group/col border-t-[1px] text-sm text-light-body-text dark:text-dark-body-text hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue hover:cursor-pointer hover:text-dark-body-text transition ease-in-out"
                  @click="$utils.Record(company).open()">
                  <template v-for="header in headers.concat({ id: '_action' })" :key="header.id + company.id">
                    <td v-if="header.id == 'checkbox'" class="relative px-6 py-3 flex items-center w-5" @click.stop="() => {}">
                      <div v-if="company.checked" class="absolute inset-y-0 left-0 w-0.5 bg-blue" />
                      <input
                        v-model="company.checked"
                        type="checkbox"
                        class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
                        @change="toggleCompanyFromSelectedList(company, company.checked)">
                    </td>
                    <td v-if="header.id == 'origin'" class="px-6 py-3 font-light capitalize">
                      {{ company?.origin_group.replace("_", " ") }}
                    </td>
                    <td v-if="header.id == 'type'" class="px-6 py-3 font-light capitalize">
                      {{ company?.type }}
                    </td>
                    <td v-if="header.id == 'name'" class="px-6 py-3 font-light capitalize">
                      {{ Record.from(company).name }}
                    </td>
                    <td v-if="header.id == 'owner'" class="px-6 py-1 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div class="flex items-center">
                          <div>
                            <img
                              v-if="$store.state.data.users.filter((user) => user.id == company.owner)[0]?.avatar"
                              class="inline-block h-9 w-9 rounded-full"
                              :src="$store.state.data.users.filter((user) => user.id == company.owner)[0]?.avatar"
                              alt="">
                            <Avatar
                              v-else
                              :first-name="$store.state.data.users.filter((user) => user.id == company.owner)[0]?.first_name"
                              :last-name="$store.state.data.users.filter((user) => user.id == company.owner)[0]?.last_name" />
                          </div>
                          <div class="ml-3">
                            <p class="text-sm dark:!text-white text-gray-700 group-hover/col:text-white">
                              {{ Record.from($store.state.data.users.filter((user) => user.id == company.owner)[0]).full_name }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td v-if="header.id == 'added_at'" class="px-6 py-3 font-light capitalize">
                      <span v-if="company?.history.length">
                        {{ $moment(company.history[company.history.length - 1].logged_at).fromNow() }}
                      </span>
                      <span v-else>
                        {{ $moment(company.last_edited).fromNow() }}
                      </span>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-show="true" class="w-full pt-1 h-[40px] overflow-visible bg-white dark:bg-dark-box-background">
            <PaginationFooter :min-page-size="5" :default-page-size="page_size" :object="pagination" @update="handlePaginationUpdate" />
          </div>
          <div v-show="false" class="pt-1 text-right">
            <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm select-none" aria-label="Pagination">
              <div
                :class="[
                  current_page == 0
                    ? 'z-10 bg-blue-50 border-blue text-blue'
                    : 'bg-white border-gray-300 dark:border-dark-divider-border text-gray-500 hover:bg-gray-50 dark:hover:bg-dark-box-background'
                ]"
                class="dark-white-container cursor-pointer rounded-l-md relative z-10 inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20"
                @click="current_page = 0">
                1
              </div>
              <div
                v-if="current_records.length > page_size"
                :class="[
                  current_page == 1
                    ? 'z-10 bg-blue-50 border-blue text-blue'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:hover:bg-dark-box-background dark:border-dark-divider-border'
                ]"
                class="dark-white-container cursor-pointer relative inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20"
                @click="current_page = 1">
                2
              </div>
              <div
                v-if="current_records.length > page_size * 2"
                :class="[
                  current_page == 2
                    ? 'z-10 bg-blue-50 border-blue text-blue'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:hover:bg-dark-box-background dark:border-dark-divider-border'
                ]"
                class="dark-white-container cursor-pointer relative hidden items-center border border-gray-300 px-4 py-2 text-sm font-medium text-gray-500 focus:z-20 md:inline-flex"
                @click="current_page = 2">
                3
              </div>
              <Menu
                v-if="current_records.length > page_size * 3"
                as="div"
                class="bg-white border-gray-300 dark:border-dark-divider-border text-gray-500 hover:bg-gray-50 dark:hover:bg-dark-box-background dark-white-container cursor-pointer rounded-r-md relative inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20">
                <MenuButton class="inline-flex rounded-md shadow-sm">
                  <span> {{ $t("View") }} </span>
                </MenuButton>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95">
                  <MenuItems
                    class="origin-bottom-left border dark:border-dark-divider-border bg-light-divider dark:bg-dark-box-background absolute bottom-5 right-0 z-50 w-40 text-center rounded-md focus:outline-none">
                    <MenuItem v-if="current_records.find((r) => r.origin_group == 'suggested')" v-slot="{ active }">
                      <router-link
                        to="/prospecting/suggested_list"
                        :class="[
                          active ? 'text-gray-900 dark:text-dark-body-text' : '',
                          'text-common-grey hover:text-gray-700 dark:hover:text-dark-body-text flex items-center px-3 py-2 text-sm'
                        ]">
                        {{ $t("Suggested Lists") }}
                      </router-link>
                    </MenuItem>
                    <MenuItem v-if="current_records.find((r) => r.origin_group == 'growth_matrix')" v-slot="{ active }">
                      <router-link
                        to="/prospecting/growth_matrix"
                        :class="[
                          active ? 'text-gray-900 dark:text-dark-body-text' : '',
                          'text-common-grey hover:text-gray-700 dark:hover:text-dark-body-text flex items-center px-3 py-2 text-sm'
                        ]">
                        {{ $t("Growth Matrix") }}
                      </router-link>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <UpdateRecord
      v-model:is-open="update_record_modal_is_open"
      :component-reference="componentReference"
      :object-type="selected_record?.type"
      :view-type="VIEW_TYPES.FORM_UPDATE"
      :selected-record="selected_record"
      :page-reference="PAGE_REFERENCES.DASHBOARD"
      @updateMap="$emitter.emit('refreshTable')" />
  </div>
</template>

<script>
import draggable from "vuedraggable"
import { markRaw } from "vue"
import dataService from "@/services/data.service"
import UpdateRecord from "@/components/shared/maps-components/UpdateRecord.vue"
import RecordActionsDropDown from "@/components/shared/RecordActionsDropDown.vue"
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue"
import PaginationFooter from "@/components/shared/PaginationFooter.vue"
import TextInput from "@/components/shared/TextInput.vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import { DatePicker } from "v-calendar"
import Avatar from "@/components/shared/Avatar.vue"
import { PAGE_REFERENCES, VIEW_TYPES } from "@/store/constants/references"
import { Record } from "@/models/record"
import { Funnel, Building2, ListTodo, Grid2x2Plus, Search, X, User } from "lucide-vue-next"
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Building2,
    X,
    DatePicker,
    TextInput,
    Search,
    User,
    ListTodo,
    MultiSelectInput,
    PaginationFooter,
    Funnel,
    draggable,
    Grid2x2Plus,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    LoadingSpinner,
    UpdateRecord,
    Avatar,
    RecordActionsDropDown
  },
  inject: ["canUserEditLayout"],
  props: {
    label: {
      type: String,
      default: "New Leads"
    },
    componentReference: {
      type: String,
      required: false
    },
    view_by: {
      type: Array,
      default: null
    },
    preservedPageSize: {
      type: Number,
      default: 5
    }
  },
  emits: ["update:selectedRecords", "update:records", "update:sorting", "update:preservedFilters", "update:pageSize"],

  data() {
    return {
      origins: [
        { name: this.$t("Suggested Lists"), value: "suggested" },
        { name: this.$t("Growth Matrix"), value: "growth_matrix" }
      ],
      types: [],
      type: [],
      origin: [],
      owner: [],
      name: "",
      date_range: null,
      user_menu_icon: User,
      status_icon: markRaw(ListTodo),
      companies_icon: markRaw(Building2),
      filter_is_open: false,
      page_size: 5,
      pagination: { count: 0, next: null, previous: null, results: [] },
      ALL_EMITTERS,
      Record,
      PAGE_REFERENCES,
      first_loading: false,
      object_type: null,
      selected_record: null,
      current_records: [],
      displayed_records: null,
      current_page: 0,
      headers: [],
      drag: false,
      all_headers: [
        {
          label: this.$t("Origin"),
          id: "origin"
        },
        {
          label: this.$t("Type"),
          id: "type"
        },
        {
          label: this.$t("Name"),
          id: "name"
        },
        {
          label: this.$t("Owner"),
          id: "owner"
        },
        {
          label: this.$t("Added at"),
          id: "added_at"
        }
      ],
      update_record_modal_is_open: false,
      select_all_records: false,
      selected_records: new Set(),
      is_loading: false,
      VIEW_TYPES
    }
  },
  computed: {
    translatedLabel() {
      return this.$t(this.label)
    },
    current_headers() {
      return this.$store.state.ui_options.leads_table_headers
    },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  watch: {
    filter_is_open() {
      if (!this.filter_is_open) {
        this.origin = []
        this.type = []
        this.owner = []
        this.name = ""
        this.date_range = null
        this.getNewLeadsCompanies()
      }
    },
    origin: {
      deep: true,
      handler() {
        this.getNewLeadsCompanies()
      }
    },
    type: {
      deep: true,
      handler() {
        this.getNewLeadsCompanies()
      }
    },
    owner: {
      deep: true,
      handler() {
        this.getNewLeadsCompanies()
      }
    },
    name() {
      this.getNewLeadsCompanies()
    },
    date_range: {
      deep: true,
      handler() {
        this.getNewLeadsCompanies()
      }
    },
    select_all_records() {
      this.current_records = this.current_records.map((company) => {
        company["checked"] = this.select_all_records
        if (this.select_all_records) {
          this.selected_records.add(company.id)
        } else {
          this.selected_records.delete(company.id)
        }
        return company
      })
      this.$emitter.emit(this.componentReference, {
        event: "selectedRecords",
        data: Array.from(this.selected_records)
      })
      this.setDropDownActions()
    },
    current_headers() {
      this.refreshTableView()
    },
    current_page() {
      this.displayed_records = this.current_records.slice(
        this.current_page * this.page_size,
        this.current_page * this.page_size + this.page_size
      )
    }
  },
  activated() {
    this.register_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  deactivated() {
    this.unregister_listeners()

    this.first_loading = false
  },
  created() {
    if (this.preservedPageSize) {
      this.page_size = this.preservedPageSize
    }
    this.register_listeners()

    this.first_loading = true

    this.refreshTableView()
    this.getNewLeadsCompanies()
  },

  methods: {
    register_listeners() {
      if (!this.first_loading) {
        this.first_loading = true
        this.$emitter.on(ALL_EMITTERS.EXPORT_LEADS_TABLE_CSV, () => {
          this.exportAsCSV("records_table")
        })
      }
      this.$emitter.on(ALL_EMITTERS.RESET_NEW_LEADS_TABLE, () => {
        this.getNewLeadsCompanies()
      })
      this.$emitter.on(ALL_EMITTERS.NEW_LEAD, (record) => {
        this.current_records.unshift(record)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.EXPORT_LEADS_TABLE_CSV)
      this.$emitter.off(ALL_EMITTERS.RESET_NEW_LEADS_TABLE)
      this.$emitter.off(ALL_EMITTERS.NEW_LEAD)
    },
    getNewLeadsCompanies() {
      this.is_loading = true
      dataService.getNewLeads().then((result) => {
        this.is_loading = false
        let arr = new Set()
        this.current_records = result.data.map((m) => {
          arr.add(m.type)
          m["checked"] = this.selected_records.has(m.id)
          return m
        })
        this.types = Array.from(arr)
        if (this.view_by) {
          this.current_records = this.current_records.filter((r) => this.view_by.includes(r.origin_group))
        }
        if (this.origin.length) {
          this.current_records = this.current_records.filter((r) => this.origin.map((x) => x.value).includes(r.origin_group))
        }
        if (this.type.length) {
          this.current_records = this.current_records.filter((r) => this.type.includes(r.type))
        }
        if (this.owner.length) {
          this.current_records = this.current_records.filter((r) => this.owner.includes(r.owner))
        }
        if (this.name) {
          this.current_records = this.current_records.filter(
            (r) => r["name"] && r["name"].toLowerCase().search(this.name.toLowerCase()) != -1
          )
        }
        if (this.date_range) {
          this.current_records = this.current_records.filter((r) => {
            let date = new Date(r.history.length ? r.history[r.history.length - 1].logged_at : r.last_edited)
            return date >= this.date_range.start && date <= this.date_range.end
          })
        }
        this.displayed_records = this.current_records.slice(
          this.current_page * this.page_size,
          this.current_page * this.page_size + this.page_size
        )
        this.pagination = {
          count: this.current_records.length,
          next: null,
          previous: null,
          results: this.current_records
        }
      })
    },
    handlePaginationUpdate({ page, page_size }) {
      if (this.page_size != page_size) {
        this.$emit("update:preservedFilters", { preservedPageSize: page_size })
        this.page_size = page_size
      }
      this.displayed_records = this.current_records.slice((page - 1) * this.page_size, (page - 1) * this.page_size + this.page_size)
      this.$emit("update:pageSize", page_size)
    },
    refreshTableView() {
      if (this.$store.state.ui_options.leads_table_headers) {
        if (this.$store.state.ui_options.leads_table_headers[this.componentReference]) {
          this.headers = this.$store.state.ui_options.leads_table_headers[this.componentReference].map((header_id) => {
            let header = this.all_headers.find((x) => x.id == header_id) || {
              id: header_id,
              label: header_id
            }
            return { ...header }
          })
        }
      } else {
        this.headers = this.all_headers.map((header) => header)
        this.$store.dispatch("ui/setUIValue", {
          leads_table_headers: {
            ...this.$store.state.ui_options.leads_table_headers,
            [this.componentReference]: this.all_headers.map((x) => x.id)
          }
        })
      }
      if (!this.editing_disabled) {
        this.headers.unshift({ label: "", id: "checkbox" })
      }
    },
    exportAsCSV(table_id, separator = ",") {
      //displaying all records to download and then return them to their original state
      let displayed_records = this.displayed_records
      this.displayed_records = this.current_records
      setTimeout(() => {
        // Select rows from table_id
        let rows = document.querySelectorAll("table#" + table_id + " tr")
        this.displayed_records = displayed_records
        // Construct csv
        let csv = []
        for (let i = 0; i < rows.length; i++) {
          let row = [],
            cols = rows[i].querySelectorAll("td, th")
          if (!rows[i].getAttribute("id") || Array.from(this.selected_records).includes(rows[i].getAttribute("id"))) {
            for (let j = 0; j < cols.length; j++) {
              // Clean innertext to remove multiple spaces and jumpline (break csv)
              let data = cols[j].innerText.replace(/(\r\n|\n|\r)/gm, "").replace(/(\s\s)/gm, " ")
              // Escape double-quote with double-double-quote (see https://stackoverflow.com/questions/17808511/properly-escape-a-double-quote-in-csv)
              data = data.replace(/"/g, '""')
              // Push escaped string
              row.push('"' + data + '"')
            }
            csv.push(row.join(separator))
          }
        }
        let csv_string = csv.join("\n")
        // Download it
        let filename = "RevSetter Leads " + new Date().toLocaleDateString() + ".csv"
        let link = document.createElement("a")
        link.style.display = "none"
        link.setAttribute("target", "_blank")
        link.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(csv_string))
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }, 100)
    },

    toggleCompanyFromSelectedList(comp, add) {
      if (add) {
        this.selected_records.add(comp.id)
      } else {
        this.selected_records.delete(comp.id)
      }
      this.$emitter.emit(this.componentReference, {
        event: "selectedRecords",
        data: Array.from(this.selected_records)
      })
      this.setDropDownActions()
    },
    setDropDownActions() {
      if (Array.from(this.selected_records).length > 0) {
        let grouped = this.$utils.groupBy(this.current_records, "type")
        if (Object.keys(grouped).length > 1) {
          this.object_type = null
        } else {
          this.object_type = Object.keys(grouped)[0]
        }
      } else {
        this.object_type = null
      }
    }
  }
}
</script>
